import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Package, ClipboardList, Loader2, Save, Trash2, Plus, X, ListChecks, Upload } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUSES = ["nouvelle", "confirmee", "en_preparation", "prete", "en_livraison", "completee", "annulee"];
const STATUS_LABEL = {
  nouvelle: "Nouvelle", confirmee: "Confirmée", en_preparation: "En préparation",
  prete: "Prête", en_livraison: "En livraison", completee: "Complétée", annulee: "Annulée",
};

export default function Admin() {
  const nav = useNavigate();
  const { user, loading, openAuth } = useAuth();
  const [tab, setTab] = useState("dashboard");

  useEffect(() => { if (!loading && !user) openAuth(); }, [loading, user, openAuth]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-coral" /></div>;
  if (!user || user.role !== "admin") return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center">
      <h1 className="font-heading text-2xl font-bold text-foreground">Accès réservé</h1>
      <p className="text-muted-foreground mt-2">Cette section est réservée à l'administration Traiteur FL.</p>
      <button onClick={() => nav("/")} className="mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-semibold">Retour à l'accueil</button>
    </div>
  );

  const tabs = [
    { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { id: "produits", label: "Produits", icon: Package },
    { id: "options", label: "Options", icon: ListChecks },
    { id: "commandes", label: "Commandes", icon: ClipboardList },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-foreground">Administration</h1>
      <div className="flex gap-1 mt-6 mb-8 border-b border-border overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-3 font-heading text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-coral text-coral" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "dashboard" && <Dashboard />}
      {tab === "produits" && <Products />}
      {tab === "options" && <OptionGroups />}
      {tab === "commandes" && <Orders />}
    </div>
  );
}

function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  if (isLoading) return <Loader />;
  const cards = [
    { label: "Commandes totales", value: data.total_orders, tone: "coral" },
    { label: "Nouvelles commandes", value: data.new_orders, tone: "leaf" },
    { label: "Produits actifs", value: data.products, tone: "coral" },
    { label: "Clients", value: data.customers, tone: "leaf" },
    { label: "Chiffre d'affaires", value: money(data.revenue), tone: "coral" },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((c) => (
        <div key={c.label} data-testid={`stat-${c.label}`} className="p-6 rounded-2xl bg-ink-800 border border-border">
          <p className="text-sm text-muted-foreground">{c.label}</p>
          <p className={`font-heading text-3xl font-extrabold mt-2 ${c.tone === "coral" ? "text-coral" : "text-foreground"}`}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function Products() {
  const qc = useQueryClient();
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: optionGroups = [] } = useQuery({ queryKey: ["option-groups"], queryFn: async () => (await api.get("/option-groups")).data });
  const [editing, setEditing] = useState(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const save = async (p) => {
    try {
      const body = { nom: p.nom, description: p.description, prix: parseFloat(p.prix), categorie_slug: p.categorie_slug,
        servings: parseInt(p.servings) || 1, configurable: !!p.configurable, image: p.image, statut: p.statut, sold_out: !!p.sold_out, ordre: p.ordre || 99, option_group_ids: p.option_group_ids || [] };
      if (p.id) await api.put(`/products/${p.id}`, body); else await api.post("/products", body);
      toast.success("Produit enregistré"); setEditing(null); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce produit ?")) return; try { await api.delete(`/products/${id}`); toast.success("Supprimé"); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleSoldOut = async (p) => { try { await api.put(`/products/${p.id}`, { ...p, sold_out: !p.sold_out }); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (isLoading) return <Loader />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-muted-foreground text-sm">{products.length} produits</p>
        <button onClick={() => setEditing({ nom: "", description: "", prix: 0, categorie_slug: categories[0]?.id || "produits-maison", servings: 1, configurable: false, image: "", statut: "Disponible", sold_out: false })}
          data-testid="admin-add-product" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-heading text-sm font-medium"><Plus className="h-4 w-4" /> Ajouter</button>
      </div>
      <div className="rounded-2xl bg-ink-800 border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-foreground/5 text-muted-foreground"><tr>
              <th className="text-left p-3 font-medium">Produit</th><th className="text-left p-3 font-medium">Catégorie</th>
              <th className="text-right p-3 font-medium">Prix</th><th className="text-center p-3 font-medium">Statut</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-t border-border">
                  <td className="p-3"><div className="flex items-center gap-2"><img src={p.image || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='36' height='36'><rect width='100%25' height='100%25' fill='%23e5e5e5'/></svg>"} alt="" className="h-9 w-9 rounded object-cover" /><span className="text-foreground">{p.nom}</span></div></td>
                  <td className="p-3 text-muted-foreground">{p.categorie_slug}</td>
                  <td className="p-3 text-right text-coral font-bold">{money(p.prix)}</td>
                  <td className="p-3 text-center">
                    <button onClick={() => toggleSoldOut(p)} className={`px-2.5 py-1 rounded-full text-xs font-medium ${p.sold_out ? "bg-red-500/20 text-red-400" : "bg-foreground/10 text-foreground"}`}>
                      {p.sold_out ? "Épuisé" : "Disponible"}
                    </button>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.id}`} className="text-muted-foreground hover:text-coral px-2">Modifier</button>
                    <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-red-400 px-2"><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <ProductModal product={editing} categories={categories} optionGroups={optionGroups} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ProductModal({ product, categories, optionGroups = [], onClose, onSave }) {
  const [p, setP] = useState({ ...product, option_group_ids: product.option_group_ids || [] });
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setP((v) => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleGroup = (gid) => setP((v) => ({ ...v, option_group_ids: v.option_group_ids.includes(gid) ? v.option_group_ids.filter((x) => x !== gid) : [...v.option_group_ids, gid] }));
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((v) => ({ ...v, image: data.url }));
      toast.success("Image téléversée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-card border border-border p-6 max-h-[90vh] overflow-y-auto" data-testid="admin-product-modal">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-heading font-bold text-foreground text-lg">{p.id ? "Modifier" : "Nouveau"} produit</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <AF label="Nom" value={p.nom} onChange={set("nom")} testid="pm-nom" />
          <label className="block"><span className="text-sm text-muted-foreground">Description</span>
            <textarea value={p.description} onChange={set("description")} rows={2} className="mt-1 w-full px-4 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground focus:border-coral focus:outline-none" /></label>
          <div className="grid grid-cols-2 gap-3">
            <AF label="Prix ($)" type="number" value={p.prix} onChange={set("prix")} testid="pm-prix" />
            <AF label="Portions" type="number" value={p.servings} onChange={set("servings")} testid="pm-servings" />
          </div>
          <label className="block"><span className="text-sm text-muted-foreground">Catégorie</span>
            <select value={p.categorie_slug} onChange={set("categorie_slug")} className="mt-1 w-full px-4 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground focus:border-coral focus:outline-none">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select></label>
          <div>
            <span className="text-sm text-muted-foreground">Photo du produit</span>
            <div className="mt-1 flex items-center gap-3">
              {p.image ? <img src={p.image} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" /> : <div className="h-16 w-16 rounded-lg bg-foreground/5 border border-border" />}
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-foreground/5 border border-border hover:bg-foreground/10 text-sm" data-testid="pm-upload">
                <Upload className="h-4 w-4" /> {uploading ? "Téléversement..." : "Téléverser une image"}
                <input type="file" accept="image/*" onChange={upload} className="hidden" />
              </label>
            </div>
            <AF label="ou URL / chemin de l'image" value={p.image} onChange={set("image")} testid="pm-image" />
          </div>
          {optionGroups.length > 0 && (
            <div>
              <span className="text-sm text-muted-foreground">Groupes d'options associés</span>
              <div className="mt-2 space-y-1.5">
                {optionGroups.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 text-sm text-foreground">
                    <input type="checkbox" checked={p.option_group_ids.includes(g.id)} onChange={() => toggleGroup(g.id)} className="accent-coral" data-testid={`pm-group-${g.id}`} />
                    {g.nom} <span className="text-muted-foreground text-xs">({g.options.length} option(s))</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={!!p.configurable} onChange={set("configurable")} className="accent-coral" /> Produit configurable (format familial)</label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={!!p.sold_out} onChange={set("sold_out")} className="accent-coral" /> Épuisé</label>
        </div>
        <button onClick={() => onSave(p)} data-testid="pm-save" className="mt-6 w-full py-3 rounded-xl bg-primary text-primary-foreground hover:opacity-90 font-heading font-semibold flex items-center justify-center gap-2"><Save className="h-4 w-4" /> Enregistrer</button>
      </div>
    </div>
  );
}

function Orders() {
  const qc = useQueryClient();
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data });
  const setStatus = async (id, status) => { try { await api.put(`/admin/orders/${id}/status`, { status }); qc.invalidateQueries({ queryKey: ["admin-orders"] }); toast.success("Statut mis à jour"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (isLoading) return <Loader />;
  if (orders.length === 0) return <div className="py-16 text-center text-muted-foreground">Aucune commande pour l'instant.</div>;

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="rounded-2xl bg-ink-800 border border-border p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-heading font-bold text-foreground">n° {o.order_number} <span className="text-muted-foreground font-normal text-sm">· {new Date(o.created_at).toLocaleString("fr-CA")}</span></p>
              <p className="text-sm text-muted-foreground mt-1">{o.contact.name} · {o.contact.phone} · {o.contact.email}</p>
              <p className="text-sm text-muted-foreground">{o.fulfillment.mode === "livraison" ? `Livraison · ${o.fulfillment.address || ""} ${o.fulfillment.city || ""} ${o.fulfillment.postal_code || ""}` : "Cueillette · 374 Saint Joseph"} · {o.fulfillment.date} {o.fulfillment.time}</p>
              {o.notes && <p className="text-sm text-muted-foreground italic mt-1">« {o.notes} »</p>}
            </div>
            <div className="text-right">
              <p className="font-heading text-xl font-bold text-coral">{money(o.total)}</p>
              <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`admin-status-${o.order_number}`}
                className="mt-2 px-3 py-1.5 rounded-lg bg-foreground/5 border border-border text-foreground text-sm focus:border-coral focus:outline-none">
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {o.items.map((i, idx) => (
              <span key={idx} className="text-xs px-2.5 py-1 rounded-full bg-foreground/5 border border-border text-muted-foreground">
                {i.quantity}× {i.nom}{i.guests ? ` (${i.guests} inv.)` : ""}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function OptionGroups() {
  const qc = useQueryClient();
  const { data: groups = [], isLoading } = useQuery({ queryKey: ["option-groups"], queryFn: async () => (await api.get("/option-groups")).data });
  const [editing, setEditing] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["option-groups"] });
  const save = async (g) => {
    try {
      const body = { nom: g.nom, type: g.type, min_select: parseInt(g.min_select) || 0, max_select: parseInt(g.max_select) || 1,
        options: (g.options || []).filter((o) => o.nom).map((o) => ({ nom: o.nom, prix_supplement: parseFloat(o.prix_supplement) || 0 })) };
      if (g.id) await api.put(`/option-groups/${g.id}`, body); else await api.post("/option-groups", body);
      toast.success("Groupe enregistré"); setEditing(null); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce groupe ?")) return; await api.delete(`/option-groups/${id}`); refresh(); };
  if (isLoading) return <Loader />;
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-muted-foreground text-sm">{groups.length} groupe(s) d'options réutilisables</p>
        <button onClick={() => setEditing({ nom: "", type: "single", min_select: 1, max_select: 1, options: [{ nom: "", prix_supplement: 0 }] })}
          data-testid="admin-add-group" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-heading text-sm font-medium"><Plus className="h-4 w-4" /> Nouveau groupe</button>
      </div>
      {groups.length === 0 ? (
        <div className="py-14 text-center text-muted-foreground text-sm">Aucun groupe d'options. Créez « Choix de pain », « Sauces », etc. et associez-les à plusieurs produits.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.id} data-testid={`option-group-${g.id}`} className="rounded-2xl bg-card border border-border p-4">
              <div className="flex justify-between items-start">
                <div><p className="font-heading font-bold text-foreground">{g.nom}</p>
                  <p className="text-xs text-muted-foreground">{g.type === "single" ? "Choix unique" : "Choix multiple"} · min {g.min_select} / max {g.max_select}</p></div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(g)} className="text-muted-foreground hover:text-coral text-sm">Modifier</button>
                  <button onClick={() => del(g.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {g.options.map((o, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-foreground/5 border border-border">{o.nom}{o.prix_supplement ? ` +${money(o.prix_supplement)}` : ""}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <OptionGroupModal group={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function OptionGroupModal({ group, onClose, onSave }) {
  const [g, setG] = useState({ ...group, options: group.options?.length ? group.options : [{ nom: "", prix_supplement: 0 }] });
  const set = (k) => (e) => setG((v) => ({ ...v, [k]: e.target.value }));
  const setOpt = (i, k, val) => setG((v) => ({ ...v, options: v.options.map((o, idx) => idx === i ? { ...o, [k]: val } : o) }));
  const addOpt = () => setG((v) => ({ ...v, options: [...v.options, { nom: "", prix_supplement: 0 }] }));
  const rmOpt = (i) => setG((v) => ({ ...v, options: v.options.filter((_, idx) => idx !== i) }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-card border border-border p-6 max-h-[90vh] overflow-y-auto" data-testid="option-group-modal">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-heading font-bold text-foreground text-lg">{g.id ? "Modifier" : "Nouveau"} groupe d'options</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <AF label="Nom (ex. Choix de pain)" value={g.nom} onChange={set("nom")} testid="og-nom" />
          <div className="grid grid-cols-3 gap-3">
            <label className="block"><span className="text-sm text-muted-foreground">Type</span>
              <select value={g.type} onChange={set("type")} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground focus:border-coral focus:outline-none">
                <option value="single">Choix unique</option><option value="multiple">Choix multiple</option>
              </select></label>
            <AF label="Min" type="number" value={g.min_select} onChange={set("min_select")} testid="og-min" />
            <AF label="Max" type="number" value={g.max_select} onChange={set("max_select")} testid="og-max" />
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Options</span>
            <div className="mt-2 space-y-2">
              {g.options.map((o, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={o.nom} onChange={(e) => setOpt(i, "nom", e.target.value)} placeholder="Nom de l'option" data-testid={`og-opt-${i}`}
                    className="flex-1 px-3 py-2 rounded-lg bg-foreground/5 border border-border text-foreground text-sm focus:border-coral focus:outline-none" />
                  <input type="number" value={o.prix_supplement} onChange={(e) => setOpt(i, "prix_supplement", e.target.value)} placeholder="+ $"
                    className="w-24 px-3 py-2 rounded-lg bg-foreground/5 border border-border text-foreground text-sm focus:border-coral focus:outline-none" />
                  <button onClick={() => rmOpt(i)} className="text-muted-foreground hover:text-red-400"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={addOpt} className="mt-2 text-sm text-coral hover:underline">+ Ajouter une option</button>
          </div>
        </div>
        <button onClick={() => onSave(g)} data-testid="og-save" className="mt-6 w-full py-3 rounded-xl bg-primary text-primary-foreground hover:opacity-90 font-heading font-semibold flex items-center justify-center gap-2"><Save className="h-4 w-4" /> Enregistrer</button>
      </div>
    </div>
  );
}

function AF({ label, testid, ...props }) {
  return <label className="block"><span className="text-sm text-muted-foreground">{label}</span>
    <input {...props} data-testid={testid} className="mt-1 w-full px-4 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground focus:border-coral focus:outline-none" /></label>;
}
function Loader() { return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-coral" /></div>; }
